import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Row, Col, Button, Alert } from "react-bootstrap";
import { setOrderServiceInfo, setOrderDeliveryInfo } from "state/orderEdition/actions";
import useServiceLineSync from "app/orderTool/hooks/useServiceLineSync";
import { calculateWaiterCount } from "helper/serviceCalculation";
import StaffMealSection from "app/orderTool/pages/multiOrders/StaffMealSection";
import ServiceTenderPriceAlert from "app/orderTool/pages/multiOrders/ServiceTenderPriceAlert";

const isTruthy = (v) => v === true || v === 1 || v === "1";
const toInt = (v, fallback = 0) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
};

const DEFAULT_SERVICE_INFO = {
  masterCount: 1,
  waiterCount: 0,
  labDeparture: "",
  labArrival: "",
  labReturn: "",
};

/**
 * Bloc Service rendu *à l'intérieur* d'une brique porteuse de l'option Service
 * (cf. `BriqueTemplate.hasServiceOption` côté backend). Remplace la section
 * "Service" globale (cachée par `ServiceInfoComponent` quand cette brique est
 * détectée).
 *
 * Contenu :
 *  - Toggle "Service nécessaire" (lié à `order.deliveryInfo.service`)
 *  - Inputs masterCount / waiterCount (liés à `order.serviceInfo`)
 *  - Tableau auto-calculé des articles service (jour + nuit) — read-only,
 *    contenu géré par `useServiceLineSync.rebuildServiceLines()`
 *  - Bouton rouge "Actualiser le calcul" si désync
 *  - Bannière rouge si conflit cross-brique (>1 brique flaggée)
 */
const BriqueServiceSection = ({ orderIndex }) => {
  const dispatch = useDispatch();
  const order = useSelector((s) => s.orderEdition.orders?.[orderIndex]);

  // Lien Google Calendar (uniquement si l'order a déjà un eventId — i.e. devis save).
  const calendarId = process.env.REACT_APP_GOOGLE_CALENDAR_ID ||
    (process.env.NODE_ENV === 'production'
      ? 'c_4f1bfbf0ca73ce205720a179678d9a0ffd646d2f910af4e8edab78f647de738f@group.calendar.google.com'
      : 'c_38e39882dfd0581db0872bda543ef51586f3def40ece66a4df552e0ff1921d6b@group.calendar.google.com');
  const calendarLink = useMemo(() => {
    const eventId = order?.googleCalendarEventId;
    if (!eventId) return null;
    if (calendarId) {
      try {
        const payload = `${eventId} ${calendarId}`;
        const encoded = window.btoa(unescape(encodeURIComponent(payload)));
        return `https://calendar.google.com/calendar/u/0/r/eventedit/${encoded}`;
      } catch (error) {
        // silent
      }
    }
    return `https://calendar.google.com/calendar/u/0/r/eventedit/${encodeURIComponent(eventId)}`;
  }, [order?.googleCalendarEventId, calendarId]);

  const {
    removeAllServiceLines,
    rebuildServiceLines,
    isServiceCalcOutOfSync,
    findServiceOptionConflicts,
    getServiceTenderPriceGaps,
    applyTenderPricesToServiceLines,
  } = useServiceLineSync(orderIndex);

  const serviceInfo = useMemo(
    () => ({ ...DEFAULT_SERVICE_INFO, ...(order?.serviceInfo || {}) }),
    [order?.serviceInfo]
  );

  const deliveryInfo = order?.deliveryInfo || {};
  const serviceEnabled = isTruthy(deliveryInfo?.service);
  const personValue = deliveryInfo?.person;
  const masterCount = toInt(serviceInfo.masterCount, 0);
  const waiterCount = toInt(serviceInfo.waiterCount, 0);

  // Heures labo (départ / arrivée / fin de rangement). Locales + sync au
  // serviceInfo via dispatch — pas de sync auto des lignes service derrière.
  const [labDeparture, setLabDeparture] = useState(serviceInfo?.labDeparture || "");
  const [labArrival,   setLabArrival]   = useState(serviceInfo?.labArrival   || "");
  const [labReturn,    setLabReturn]    = useState(serviceInfo?.labReturn    || "");
  useEffect(() => {
    setLabDeparture(serviceInfo?.labDeparture || "");
    setLabArrival(serviceInfo?.labArrival || "");
    setLabReturn(serviceInfo?.labReturn || "");
  }, [serviceInfo]);

  // Cross-brique : >1 brique flaggée → bloque le toggle.
  const conflicts = findServiceOptionConflicts();
  const hasConflict = conflicts.length > 0;

  // Nombre de serveurs suggéré par la formule (premier palier de 50 = maître seul).
  const suggestedWaiterCount = calculateWaiterCount(personValue);
  // Désync : lignes ≠ compteurs, OU le nb de serveurs ne correspond plus à la
  // formule (le nb de convives a changé) → on propose le recalcul via le bouton
  // (plus de mise à jour temps réel).
  const outOfSync = serviceEnabled && !hasConflict
    && (isServiceCalcOutOfSync(masterCount, waiterCount) || waiterCount !== suggestedWaiterCount);

  // Lignes service dont le prix ne suit pas le tarif de l'AO sélectionné.
  const tenderPriceGaps = serviceEnabled ? getServiceTenderPriceGaps() : [];

  const handleUpdate = useCallback((patch) => {
    dispatch(setOrderServiceInfo(orderIndex, { ...serviceInfo, ...patch }));
  }, [dispatch, orderIndex, serviceInfo]);

  const updateServiceFlag = useCallback((enabled) => {
    dispatch(setOrderDeliveryInfo(orderIndex, { ...deliveryInfo, service: enabled ? 1 : 0 }));
    if (enabled) {
      const m = masterCount > 0 ? masterCount : 1;
      const w = waiterCount > 0 ? waiterCount : calculateWaiterCount(personValue);
      dispatch(setOrderServiceInfo(orderIndex, { ...serviceInfo, masterCount: m, waiterCount: w }));
      // Création immédiate des lignes — pas de sync auto sur change ultérieur,
      // le bouton "Actualiser le calcul" prend le relais.
      rebuildServiceLines(m, w);
    } else {
      removeAllServiceLines();
      dispatch(setOrderServiceInfo(orderIndex, { ...serviceInfo, masterCount: 1, waiterCount: 0 }));
    }
  }, [dispatch, orderIndex, deliveryInfo, serviceInfo, masterCount, waiterCount, personValue, removeAllServiceLines, rebuildServiceLines]);

  const handleNumberChange = (field, minValue = 0) => (event) => {
    const raw = event.target.value;
    if (raw === "") {
      handleUpdate({ [field]: "" });
      return;
    }
    const parsed = parseInt(raw, 10);
    const safeValue = Number.isNaN(parsed) ? minValue : Math.max(minValue, parsed);
    handleUpdate({ [field]: safeValue });
  };

  const handleLabTimeChange = (field, value) => {
    switch (field) {
      case "labDeparture": setLabDeparture(value); break;
      case "labArrival":   setLabArrival(value);   break;
      case "labReturn":    setLabReturn(value);    break;
      default: return;
    }
    handleUpdate({ [field]: value || "" });
  };

  // Les lignes service auto-calculées sont injectées dans la brique en tant
  // qu'items normaux (visibles via `BriqueItemProduct` plus bas dans le rendu
  // de la brique). On évite ici la duplication visuelle d'un tableau récap.

  return (
    <div className="mb-3" style={{ background: "#f8f9fa", padding: "12px", borderRadius: "4px", border: "1px solid #dee2e6" }}>
      {calendarLink && (
        <Alert variant="info" style={{ marginBottom: "10px" }}>
          Événement Google Calendar :{" "}
          <a href={calendarLink} target="_blank" rel="noopener noreferrer">ouvrir dans un nouvel onglet</a>
        </Alert>
      )}
      {hasConflict && (
        <Alert variant="danger" className="mb-2">
          <strong>⚠️ Plusieurs briques portent l'option Service</strong>
          <div style={{ marginTop: "4px", fontSize: "0.9em" }}>
            Une seule brique par devis peut porter cette option. Désactive
            l'option "Brique porteuse de l'option Service" sur l'une des
            briques suivantes en BO Sonata : <strong>{conflicts.join(", ")}</strong>.
          </div>
        </Alert>
      )}

      <Form.Check
        type="switch"
        id={`brique-service-needed-${orderIndex}`}
        label={<strong>Service nécessaire</strong>}
        checked={serviceEnabled}
        onChange={(e) => updateServiceFlag(e.target.checked)}
        disabled={hasConflict}
      />

      {outOfSync && (
        <Alert variant="danger" style={{ marginTop: "8px", marginBottom: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
            <span>
              <strong>Calcul service obsolète</strong> — les paramètres ont changé.
            </span>
            <Button variant="danger" size="sm" onClick={() => {
              // Recalcule le nb de serveurs depuis la formule + reconstruit les lignes.
              const w = calculateWaiterCount(personValue);
              dispatch(setOrderServiceInfo(orderIndex, { ...serviceInfo, waiterCount: w }));
              rebuildServiceLines(masterCount, w);
            }}>
              Actualiser le calcul
            </Button>
          </div>
        </Alert>
      )}

      {serviceEnabled && (
        <ServiceTenderPriceAlert gaps={tenderPriceGaps} onApply={applyTenderPricesToServiceLines} />
      )}

      {serviceEnabled && (
        <>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group controlId={`brique-svc-master-${orderIndex}`}>
                <Form.Label>Nombre de maître(s) d'hôtel</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={serviceInfo.masterCount === "" ? "" : serviceInfo.masterCount}
                  onChange={handleNumberChange("masterCount", 1)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId={`brique-svc-waiters-${orderIndex}`}>
                <Form.Label>Nombre de serveurs</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={serviceInfo.waiterCount === "" ? "" : serviceInfo.waiterCount}
                  onChange={handleNumberChange("waiterCount", 0)}
                />
              </Form.Group>
            </Col>
          </Row>

          <hr style={{ marginTop: "15px", marginBottom: "15px" }} />
          <Alert variant="info" style={{ fontSize: "0.9em", marginBottom: "15px" }}>
            <strong>Note :</strong> Les champs "Arrivée labo" et "Retour labo" définissent l'horaire de l'événement dans l'agenda de prestation de service.
          </Alert>
          <Row>
            <Col md={6}>
              <Form.Group controlId={`brique-lab-arrival-${orderIndex}`}>
                <Form.Label>
                  Arrivée labo <small className="text-muted">(définit l'horaire de début de l'événement)</small>
                </Form.Label>
                <Form.Control
                  type="time"
                  value={labArrival}
                  onChange={(e) => handleLabTimeChange("labArrival", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId={`brique-lab-departure-${orderIndex}`}>
                <Form.Label>Départ du labo</Form.Label>
                <Form.Control
                  type="time"
                  value={labDeparture}
                  onChange={(e) => handleLabTimeChange("labDeparture", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group controlId={`brique-lab-return-${orderIndex}`}>
                <Form.Label>
                  Fin de rangement labo <small className="text-muted">(définit l'horaire de fin de l'événement)</small>
                </Form.Label>
                <Form.Control
                  type="time"
                  value={labReturn}
                  onChange={(e) => handleLabTimeChange("labReturn", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <StaffMealSection orderIndex={orderIndex} />
        </>
      )}
    </div>
  );
};

export default BriqueServiceSection;
